import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, throwError } from 'rxjs';
import { ErrorService, NormalisedHttpError } from '../../error/error.service';
import {
  OTHER_CULTURES_MAX_SELECTED,
  PRIMARY_CULTURES_MAX_SELECTED,
} from './cultures-limits.type';

type ExpertCultureSelectionApiResponse = {
  data: {
    primary_culture_ids: number[];
    other_culture_ids: number[];
  };
};

type ExpertCultureSelectionRequest = {
  primary_culture_ids: number[];
  other_culture_ids: number[];
};

type NormalisedCulturesError = NormalisedHttpError;

@Injectable({
  providedIn: 'root',
})
export class ProfessionalCulturesService {
  private readonly http = inject(HttpClient);
  private readonly errorService = inject(ErrorService);

  private readonly _selectedPrimaryCultureIds = signal<number[]>([]);
  private readonly _selectedOtherCultureIds = signal<number[]>([]);
  private readonly _pendingCultureIds = signal<number[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  private loaded = false;

  readonly selectedPrimaryCultureIds = this._selectedPrimaryCultureIds.asReadonly();
  readonly selectedOtherCultureIds = this._selectedOtherCultureIds.asReadonly();
  readonly pendingCultureIds = this._pendingCultureIds.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  loadSelection(freshFromServer = false): Observable<void> {
    if (!freshFromServer && this.loaded) {
      return of(void 0);
    }

    this._loading.set(true);
    this._error.set(null);

    return this.http.get<ExpertCultureSelectionApiResponse>('api/v1/user/expert-cultures').pipe(
      map((selection) => {
        this._selectedPrimaryCultureIds.set(this.normaliseIds(selection?.data?.primary_culture_ids ?? []));
        this._selectedOtherCultureIds.set(this.normaliseIds(selection?.data?.other_culture_ids ?? []));
        this.loaded = true;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        const normalisedError = this.errorService.normaliseHttpError(errorResponse, 'profile.cultures_load_failed');
        this._error.set(normalisedError.message);
        return throwError(() => normalisedError);
      }),
      finalize(() => this._loading.set(false)),
    );
  }

  addToPrimary(cultureId: number): Observable<void> {
    const currentPrimaryCultureIds = [...this._selectedPrimaryCultureIds()];
    const currentOtherCultureIds = [...this._selectedOtherCultureIds()];

    if (currentPrimaryCultureIds.includes(cultureId)) {
      return of(void 0);
    }

    if (currentPrimaryCultureIds.length >= PRIMARY_CULTURES_MAX_SELECTED) {
      const normalisedError: NormalisedCulturesError = {
        message: 'profile.cultures_primary_limit_reached',
        status: 422,
      };
      this._error.set(normalisedError.message);
      return throwError(() => normalisedError);
    }

    const nextPrimaryCultureIds = [...currentPrimaryCultureIds, cultureId];
    const nextOtherCultureIds = currentOtherCultureIds.filter((otherCultureId) => otherCultureId !== cultureId);
    return this.persistSelections(nextPrimaryCultureIds, nextOtherCultureIds, cultureId, currentPrimaryCultureIds, currentOtherCultureIds);
  }

  addToOther(cultureId: number): Observable<void> {
    const currentPrimaryCultureIds = [...this._selectedPrimaryCultureIds()];
    const currentOtherCultureIds = [...this._selectedOtherCultureIds()];

    if (currentOtherCultureIds.includes(cultureId)) {
      return of(void 0);
    }

    if (currentOtherCultureIds.length >= OTHER_CULTURES_MAX_SELECTED) {
      const normalisedError: NormalisedCulturesError = {
        message: 'profile.cultures_other_limit_reached',
        status: 422,
      };
      this._error.set(normalisedError.message);
      return throwError(() => normalisedError);
    }

    const nextPrimaryCultureIds = currentPrimaryCultureIds.filter((primaryCultureId) => primaryCultureId !== cultureId);
    const nextOtherCultureIds = [...currentOtherCultureIds, cultureId];
    return this.persistSelections(nextPrimaryCultureIds, nextOtherCultureIds, cultureId, currentPrimaryCultureIds, currentOtherCultureIds);
  }

  removeFromPrimary(cultureId: number): Observable<void> {
    const currentPrimaryCultureIds = [...this._selectedPrimaryCultureIds()];
    const currentOtherCultureIds = [...this._selectedOtherCultureIds()];
    const nextPrimaryCultureIds = currentPrimaryCultureIds.filter((primaryCultureId) => primaryCultureId !== cultureId);
    return this.persistSelections(nextPrimaryCultureIds, currentOtherCultureIds, cultureId, currentPrimaryCultureIds, currentOtherCultureIds);
  }

  removeFromOther(cultureId: number): Observable<void> {
    const currentPrimaryCultureIds = [...this._selectedPrimaryCultureIds()];
    const currentOtherCultureIds = [...this._selectedOtherCultureIds()];
    const nextOtherCultureIds = currentOtherCultureIds.filter((otherCultureId) => otherCultureId !== cultureId);
    return this.persistSelections(currentPrimaryCultureIds, nextOtherCultureIds, cultureId, currentPrimaryCultureIds, currentOtherCultureIds);
  }

  private persistSelections(
    nextPrimaryCultureIds: number[],
    nextOtherCultureIds: number[],
    pendingCultureId: number,
    previousPrimaryCultureIds: number[],
    previousOtherCultureIds: number[],
  ): Observable<void> {
    const normalisedError = this.validateSelection(nextPrimaryCultureIds, nextOtherCultureIds);
    if (normalisedError) {
      this._error.set(normalisedError.message);
      return throwError(() => normalisedError);
    }

    this._pendingCultureIds.set([...this._pendingCultureIds(), pendingCultureId]);
    this._selectedPrimaryCultureIds.set(nextPrimaryCultureIds);
    this._selectedOtherCultureIds.set(nextOtherCultureIds);
    this._error.set(null);

    return this.http.put<ExpertCultureSelectionApiResponse>('api/v1/user/expert-cultures', {
      primary_culture_ids: nextPrimaryCultureIds,
      other_culture_ids: nextOtherCultureIds,
    } as ExpertCultureSelectionRequest).pipe(
      map((response) => {
        this._selectedPrimaryCultureIds.set(this.normaliseIds(response?.data?.primary_culture_ids ?? nextPrimaryCultureIds));
        this._selectedOtherCultureIds.set(this.normaliseIds(response?.data?.other_culture_ids ?? nextOtherCultureIds));
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        this._selectedPrimaryCultureIds.set(previousPrimaryCultureIds);
        this._selectedOtherCultureIds.set(previousOtherCultureIds);
        const normalisedErrorResponse = this.errorService.normaliseHttpError(errorResponse, 'profile.cultures_save_failed');
        this._error.set(normalisedErrorResponse.message);
        return throwError(() => normalisedErrorResponse);
      }),
      finalize(() => {
        this._pendingCultureIds.set(this._pendingCultureIds().filter((cultureId) => cultureId !== pendingCultureId));
      }),
    );
  }

  private validateSelection(primaryCultureIds: number[], otherCultureIds: number[]): NormalisedCulturesError | null {
    const hasOverlap = primaryCultureIds.some((cultureId) => otherCultureIds.includes(cultureId));
    if (primaryCultureIds.length > PRIMARY_CULTURES_MAX_SELECTED) {
      return {
        message: 'profile.cultures_primary_limit_reached',
        status: 422,
      };
    }

    if (otherCultureIds.length > OTHER_CULTURES_MAX_SELECTED) {
      return {
        message: 'profile.cultures_other_limit_reached',
        status: 422,
      };
    }

    if (hasOverlap) {
      return {
        message: 'profile.cultures_overlap_not_allowed',
        status: 422,
      };
    }

    if (new Set(primaryCultureIds).size !== primaryCultureIds.length || new Set(otherCultureIds).size !== otherCultureIds.length) {
      return {
        message: 'profile.cultures_duplicates_not_allowed',
        status: 422,
      };
    }

    return null;
  }

  private normaliseIds(ids: number[]): number[] {
    return Array.from(new Set(ids.filter((value) => Number.isFinite(value))));
  }
}
